import { Id, UpdateCustomEventParams } from '@nextforce/api'
import { useTheme } from '@rneui/themed'
import { t } from 'app/i18l'
import { useRoutingContext } from 'app/routing_context'
import { EditCustomEventScreenProps } from 'home/stack/types'
import { useCallback, useLayoutEffect, useState } from 'react'
import { errorAlert } from 'shared/alerts'
import { StatusBoundary } from 'shared/components/status_boundary'
import { showHeaderButton } from 'shared/header/header_button'
import { showHeaderTitle } from 'shared/header/header_title'
import { useRecentStoresContext } from 'stores/context'
import { useStores } from 'stores/queries/list'
import { useAddCustomEvent } from './queries/add'
import { useDeleteEvent } from './queries/delete'
import { useCustomEvent } from './queries/detail'
import { useUpdateCustomEvent } from './queries/update'
import { EditCustomEventView } from './views/edit_custom_event_view'

export function EditCustomEventScreen({
  navigation,
  route,
}: EditCustomEventScreenProps) {
  const { theme } = useTheme()
  const { data: stores } = useStores(data => data.stores)
  const { activeStoreId } = useRecentStoresContext()
  const { handleNavigation } = useRoutingContext()

  const customEventQuery = useCustomEvent(
    {
      id: route.params?.customEventId,
    },
    !!route.params?.customEventId
  )

  const customEvent = customEventQuery.data
  const activeStore = stores?.find(s => s.id === activeStoreId)

  const selectedStores = stores?.filter(s => {
    return customEvent?.storeIds?.includes(s.id)
  })

  const deleteEventMutation = useDeleteEvent()

  const [customEventParams, setCustomEventParams] =
    useState<UpdateCustomEventParams>({
      startDate: '',
      endDate: '',
      description: '',
      summary: '',
      recurring: false,
      eventType: 'CUSTOM_EVENT',
      deleted: false,
      visibleToRoles: 'ALL',
      frequency: 'DAILY',
      storeIds: activeStore ? [activeStore.id] : undefined,
      blockRto: false,
      selectedStoreIds: [],
      deselectedStoreIds: [],
    })

  const [loading, setLoading] = useState<boolean>(false)
  const [deleting, setDeleting] = useState<boolean>(false)
  const [canSaveEvent, setCanSaveEvent] = useState<boolean>(false)

  const addCustomEventMutation = useAddCustomEvent()
  const editCustomEventMutation = useUpdateCustomEvent()

  const onPressRecentChanges = useCallback(() => {
    handleNavigation({
      routeName: 'event_changes',
      routeParams: {
        customEventId: customEvent?.id,
      },
    })
  }, [customEvent?.id, handleNavigation])

  const onAddEvent = useCallback(async () => {
    setLoading(true)
    try {
      await addCustomEventMutation.mutateAsync({
        ...customEventParams,
      })
      setLoading(false)

      handleNavigation({
        routeName: 'schedule',
        routeParams: {
          date: customEvent?.startDate,
        },
      })
    } catch (err: any) {
      setLoading(false)
      console.error(err)
      errorAlert(err, t('addCustomEventErrorTitle'))
    }
  }, [
    addCustomEventMutation,
    customEvent?.startDate,
    customEventParams,
    handleNavigation,
    navigation,
  ])

  const onEditEvent = useCallback(async () => {
    if (!customEvent) {
      return
    }
    setLoading(true)
    try {
      await editCustomEventMutation.mutateAsync({
        ...customEventParams,
        id: Number(customEvent.id),
      })
      setLoading(false)
      handleNavigation({
        routeName: 'schedule',
        routeParams: {
          date: customEvent?.startDate,
        },
      })
    } catch (err: any) {
      setLoading(false)
      console.error(err)
      errorAlert(err, t('editCustomEventErrorTitle'))
    }
  }, [
    customEvent,
    customEventParams,
    editCustomEventMutation,
    handleNavigation,
  ])

  const onDeleteEvent = useCallback(
    async (id: Id) => {
      setDeleting(true)
      try {
        await deleteEventMutation.mutateAsync(id)
        setDeleting(false)
        handleNavigation({
          routeName: 'schedule',
          routeParams: {
            date: customEvent?.startDate,
          },
        })
      } catch (err: any) {
        setDeleting(false)
        console.error(err)
        errorAlert(err, t('deleteEventErrorTitle'))
      }
    },
    [customEvent?.startDate, deleteEventMutation, handleNavigation]
  )

  useLayoutEffect(() => {
    showHeaderTitle(
      navigation,
      route.params?.customEventId ? t('editEvent') : t('addNewEvent')
    )
    showHeaderButton({
      navigation,
      theme,
      title: customEvent ? t('save') : t('create'),
      onPress: customEvent ? onEditEvent : onAddEvent,
      position: 'right',
      disabled: !canSaveEvent,
    })
  }, [
    canSaveEvent,
    customEvent,
    navigation,
    onAddEvent,
    onEditEvent,
    route.params?.customEventId,
    theme,
  ])

  return (
    <StatusBoundary
      isLoading={customEventQuery.isLoading || loading}
      error={customEventQuery.error}
    >
      <EditCustomEventView
        stores={stores}
        activeStore={activeStore}
        selectedStores={selectedStores}
        customEvent={customEvent}
        onSubmit={customEvent ? onEditEvent : onAddEvent}
        onCustomEventParamsChanged={setCustomEventParams}
        loading={loading}
        onDelete={onDeleteEvent}
        deleting={deleting}
        setCanSaveEvent={setCanSaveEvent}
        onPressRecentChanges={onPressRecentChanges}
      />
    </StatusBoundary>
  )
}
